import type { FileRelationFields } from '@condo/domain';
import { condoFileSlug } from '@condo/domain';
import axios from 'axios';
import { getClient } from '@/shared/api/client';

export interface SignedUrlResult {
    bucketFilename: string;
    url: string;
}

export interface SignedUrlRequestOptions {
    signal?: AbortSignal;
}

export const getSignedUrls = async (
    files: Array<{
        uuid?: string;
        fileName: string;
        contentType: string;
        extensionHeaders?: Record<string, string>;
    }>,
    options?: SignedUrlRequestOptions,
): Promise<SignedUrlResult[]> =>
    getClient('basecamp')
        .post(
            '/file/signed-urls',
            {
                files: files.map(f => ({
                    uuid: f.uuid ?? condoFileSlug(f.fileName),
                    fileName: f.fileName,
                    contentType: f.contentType,
                    extensionHeaders: f.extensionHeaders ?? { 'x-goog-meta-filename': encodeURI(f.fileName) },
                })),
            },
            { signal: options?.signal },
        )
        .then(response => response.data);

export const getSignedDownloadUrl = async (uuid: string, filename: string): Promise<{ bucketFilename: string; url: string }> =>
    getClient('basecamp')
        .get('/file/signed-url', {
            params: { uuid, action: 'read', version: 'v2', filename },
        })
        .then(response => response.data);

/**
 * Get a signed URL for reading a file from its full bucket URL.
 * Used for durable job results that return bucket URLs directly.
 */
export const getSignedReadUrl = async (fullBucketUrl: string): Promise<{ url: string }> =>
    getClient('basecamp')
        .get('/file/signed-read-url', {
            params: { fullBucketUrl },
        })
        .then(response => response.data);

export const uploadFilesWithSignedUrls = async (
    files: File[],
    relations?: FileRelationFields,
    options?: SignedUrlRequestOptions,
): Promise<SignedUrlResult[]> => {
    const signedUrls = await getSignedUrls(
        files.map(file => ({
            uuid: condoFileSlug(file.name, relations),
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            extensionHeaders: { 'x-goog-meta-filename': encodeURI(file.name) },
        })),
        options,
    );
    const uploadableFiles = files.map((file, index) => ({ file, signedUrl: signedUrls[index] }));
    await Promise.all(
        uploadableFiles.map(async ({ file, signedUrl }) => {
            await axios.put(signedUrl.url, file, {
                headers: {
                    'Content-Type': file.type || 'application/octet-stream',
                    'Access-Control-Allow-Origin': '*',
                    'x-goog-meta-filename': encodeURI(file.name),
                },
                signal: options?.signal,
            });
        }),
    );
    return signedUrls;
};
